import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { formatCurrency, formatPercent } from "@/utils/formatters";
import { formatDate } from "@/utils/timezone";

const LaggardAnalysisModal = ({ accountId, laggards, onClose }) => {
  const [sortField, setSortField] = useState("momentum_score");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "momentum_score" ? "asc" : "desc");
    }
  };

  const sortedLaggards = useMemo(() => {
    if (!laggards) return [];

    return [...laggards].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [laggards, sortField, sortDirection]);

  const getMomentumSeverity = (score) => {
    if (score <= -200)
      return { label: "Critical", color: "text-red-600 dark:text-red-400" };
    if (score <= -100)
      return { label: "Severe", color: "text-orange-600 dark:text-orange-400" };
    if (score <= -50)
      return {
        label: "Moderate",
        color: "text-yellow-600 dark:text-yellow-400",
      };
    return { label: "Mild", color: "text-yellow-500 dark:text-yellow-500" };
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <span className="text-gray-400 dark:text-gray-600 ml-1">↕</span>;
    }
    return (
      <span className="text-blue-600 dark:text-blue-400 ml-1">
        {sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Momentum Laggard Analysis
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {laggards.length} holdings showing momentum decay
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Explanation */}
        <div className="px-6 py-4 bg-blue-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
            What is a Momentum Laggard?
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            A momentum laggard is a stock whose recent performance (1-3 years)
            is significantly weaker than its lifetime performance. The{" "}
            <span className="font-semibold">momentum score</span> compares
            recent CAGRs to lifetime CAGR—negative scores indicate slowing
            momentum, suggesting stocks that may have had strong early gains but
            have stagnated.
          </p>
          <div className="mt-2 flex gap-4 text-xs">
            <span className="text-red-600 dark:text-red-400 font-medium">
              Critical (&lt; -200)
            </span>
            <span className="text-orange-600 dark:text-orange-400 font-medium">
              Severe (-100 to -200)
            </span>
            <span className="text-yellow-600 dark:text-yellow-400 font-medium">
              Moderate (-50 to -100)
            </span>
            <span className="text-yellow-500 dark:text-yellow-500 font-medium">
              Mild (0 to -50)
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto p-6">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
              <tr>
                <th
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("ticker")}
                >
                  Ticker <SortIcon field="ticker" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("momentum_score")}
                >
                  Momentum Score <SortIcon field="momentum_score" />
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Severity
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("lifetime_cagr")}
                >
                  Lifetime CAGR <SortIcon field="lifetime_cagr" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("cagr_1y")}
                >
                  1Y CAGR <SortIcon field="cagr_1y" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("cagr_2y")}
                >
                  2Y CAGR <SortIcon field="cagr_2y" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("cagr_3y")}
                >
                  3Y CAGR <SortIcon field="cagr_3y" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("spy_lifetime_cagr")}
                >
                  SPY CAGR <SortIcon field="spy_lifetime_cagr" />
                </th>
                <th
                  className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("outperformed_spy")}
                >
                  vs SPY <SortIcon field="outperformed_spy" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("opportunity_cost_dollars")}
                >
                  Opp. Cost <SortIcon field="opportunity_cost_dollars" />
                </th>
                <th
                  className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort("holding_period_years")}
                >
                  Years Held <SortIcon field="holding_period_years" />
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {sortedLaggards.map((laggard) => {
                const severity = getMomentumSeverity(
                  laggard.momentum_score || 0
                );
                return (
                  <tr
                    key={laggard.ticker}
                    className="hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Link
                        to={`/ticker/${laggard.ticker}?accountId=${accountId}`}
                        className="font-semibold text-gray-900 dark:text-white hover:underline cursor-pointer"
                      >
                        {laggard.ticker}
                      </Link>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <span className={`font-semibold ${severity.color}`}>
                        {(laggard.momentum_score ?? 0).toFixed(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          (laggard.momentum_score ?? 0) <= -200
                            ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            : (laggard.momentum_score ?? 0) <= -100
                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                            : (laggard.momentum_score ?? 0) <= -50
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                            : "bg-yellow-50 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300"
                        }`}
                      >
                        {severity.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatPercent(laggard.lifetime_cagr ?? 0)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <span
                        className={
                          (laggard.cagr_1y ?? 0) < 0
                            ? "text-red-600 dark:text-red-400"
                            : "text-gray-900 dark:text-white"
                        }
                      >
                        {formatPercent(laggard.cagr_1y ?? 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <span
                        className={
                          (laggard.cagr_2y ?? 0) < 0
                            ? "text-red-600 dark:text-red-400"
                            : "text-gray-900 dark:text-white"
                        }
                      >
                        {formatPercent(laggard.cagr_2y ?? 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <span
                        className={
                          (laggard.cagr_3y ?? 0) < 0
                            ? "text-red-600 dark:text-red-400"
                            : "text-gray-900 dark:text-white"
                        }
                      >
                        {formatPercent(laggard.cagr_3y ?? 0)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {laggard.spy_lifetime_cagr !== null
                        ? formatPercent(laggard.spy_lifetime_cagr)
                        : "N/A"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      {laggard.outperformed_spy !== null ? (
                        laggard.outperformed_spy ? (
                          <span className="text-green-600 dark:text-green-400 font-semibold">
                            ✓
                          </span>
                        ) : (
                          <span className="text-red-600 dark:text-red-400 font-semibold">
                            ✗
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      {laggard.opportunity_cost_dollars !== null ? (
                        <span
                          className={
                            laggard.opportunity_cost_dollars >= 0
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }
                        >
                          {laggard.opportunity_cost_dollars >= 0 ? "+" : ""}
                          {formatCurrency(laggard.opportunity_cost_dollars)}
                        </span>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {(laggard.holding_period_years ?? 0).toFixed(1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex justify-between items-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Momentum Score = (1Y CAGR / Lifetime CAGR) × 50% + (2Y CAGR /
              Lifetime CAGR) × 30% + (3Y CAGR / Lifetime CAGR) × 20% - 100
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaggardAnalysisModal;
